import Typography from "aldoo-ra/Typography"
import MenuGroup from "aldoo-ra/MenuGroup"
import Button from "aldoo-ra/Button"
import FeatureTabs from "./FeatureTabs"
import parse from "html-react-parser"
import { useState, useEffect, useRef } from "react"
import useSitePlatform from "@components/Site/useSitePlatform"

// This component is visible for lg screens and above
const FeatureSection = ({ controller }) => {
  const { selectedFeature, getData, selectFeatureByID } = controller
  const [isMobile, setIsMobile] = useState(false)
  const [imageHeight, setImageHeight] = useState("450px")
  const [imageMarginRight, setImageMarginRight] = useState("0px")
  const [platform] = useSitePlatform()
  const containerRef = useRef(null)

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth
      const screenHeight = window.innerHeight
      setIsMobile(screenWidth <= 1279) // Breakpoint for smallest screens

      // Calculate responsive image height based on viewport height
      if (screenWidth >= 1280) {
        // lg screens and above
        // Calculate as a percentage of viewport height, minus header and margins
        // The 80px represents the header height and 40px for margins
        // Adding extra bottom margin (increased from 100 to 140px)
        const calculatedHeight = Math.min(
          screenHeight * 0.55, // Reduced from 60% to 55% of screen height
          screenHeight - 80 - 40 - 140 // Extra bottom buffer space for laptops
        )

        // Scale based on breakpoints, with extra adjustment for laptop screens
        if (screenWidth >= 1680) {
          // 2xl
          setImageHeight(`${calculatedHeight * 1.05}px`)
          setImageMarginRight(window.innerWidth * 0.03 + "px")
        } else if (screenWidth >= 1280) {
          // xl
          setImageHeight(`${calculatedHeight}px`)
          setImageMarginRight(window.innerWidth * 0.09 + "px")
        } else {
          // lg
          setImageHeight(`${calculatedHeight * 0.92}px`) // Further reduced for laptop screens
        }
      }
    }

    // Handle SSR
    if (typeof window !== "undefined") {
      handleResize()
      window.addEventListener("resize", handleResize)
      return () => {
        window.removeEventListener("resize", handleResize)
      }
    }
  }, [])

  // Get sorted features using getData(), ensuring it's always an array
  let features = getData() || []

  //filter the features for the current group only
  if (!isMobile)
    features = features.filter((feature) =>
      feature.group?.some((g) => g.id === controller.selectedGroup)
    )

  if (!selectedFeature || !features.length) return null

  const { name, image, secondary_image } = selectedFeature
  let { info } = selectedFeature
  //convert to array if not already
  !Array.isArray(info) && info && (info = [info])

  return (
    <div
      className="relative flex flex-col w-full max-w-[1250px] lg:min-h-[calc(100vh-80px-40px)] lg:gap-8 mx-auto"
      ref={containerRef}
    >
      {/* Features Tab Navigation   */}
      <FeatureTabs
        features={features}
        selectedFeatureID={selectedFeature?.id}
        onFeatureSelect={(featureId) => {
          selectFeatureByID(featureId)
        }}
      />

      {/* Feature Content */}
      <div className="flex flex-col-reverse lg:flex-row items-center lg:items-start justify-between pl-2 xl:pl-6">
        {/* Left side content */}
        <div className="flex flex-col items-center lg:items-start justify-between w-full lg:w-[65%] xl:w-[50%] 2xl:w-[65%] md:max-w-[700px] lg:max-w-[600px] xl:max-w-[620px] 2xl:max-w-[700px] lg:pt-5">
          <div>
            {info?.map((text, index) => (
              <Typography
                component={"div"}
                key={index}
                variant="body_20"
                className="w-full text-text mb-4"
              >
                {parse(text)}
              </Typography>
            ))}
          </div>

          <div className="mt-[48px] mx-auto lg:ml-0 lg:mb-0">
            <MenuGroup
              name="TryAppNow"
              renderAs="hlist"
              itemsAsLinks={false}
              styleOverrides={{
                menu_item: "px-0 mx-0 my-0",
              }}
              menuItemRender={(item) => {
                const url =
                  (item?.link[0]?.parameters &&
                    item?.link[0]?.parameters[platform]) ||
                  item?.link[0]?.url

                return (
                  <Button className="w-auto h-[52px] md:h-[48px] flex items-center justify-center bg-primary hover:bg-primary_hov text-white text-[24px] md:text-[20px] font-bold rounded-full px-[16px] md:px-[18px] xl:px-[20px] transition ">
                    <a href={url} target="_blank">
                      {item?.label}
                    </a>
                  </Button>
                )
              }}
            />
          </div>
        </div>

        {/* Right side image - Responsive height container with bottom margin */}
        <div
          className="overflow-hidden flex w-[400px]  justify-end mb-6 lg:mb-8 "
          style={{
            height: imageHeight,
            marginRight: imageMarginRight,
          }}
        >
          <img
            src={secondary_image ?? image}
            alt={name}
            className="flex justify-end h-full object-contain"
          />
        </div>

        {/* Invisible box to keep the arrow menu away from image and distribute text and image properly at diff screen-widths */}
        <div className="relative hidden xl:block 2xl:block flex-shrink-0 w-[12%] 2xl:w-[4%] h-[500px]"></div>
      </div>
    </div>
  )
}

export default FeatureSection
